// ponytail: dates are "YYYY-MM-DD" strings and times "HH:MM" strings, never Date
// objects — a series runs in its venue's zone, not the browser's. Swap for
// zone-aware values when the backend that persists these lands.

import { v4 as uuidv4 } from "uuid";
import { coverage, slotKeyFor, ticketSetKeyOf, ticketSetProblems, ticketSetsOf, typesInSet, withoutTicketSet } from "./ticketSets";

export const WEEKDAYS = [
  { value: 0, short: "Sun", long: "Sunday" },
  { value: 1, short: "Mon", long: "Monday" },
  { value: 2, short: "Tue", long: "Tuesday" },
  { value: 3, short: "Wed", long: "Wednesday" },
  { value: 4, short: "Thu", long: "Thursday" },
  { value: 5, short: "Fri", long: "Friday" },
  { value: 6, short: "Sat", long: "Saturday" },
];

export const INTERVAL_OPTIONS = [15, 20, 30, 45, 60, 90, 120];

export const MAX_GENERATED_DATES = 400;
export const MAX_PLANNED_TICKET_TYPES = 200;

const atLocalNoon = (isoDate) => new Date(`${isoDate}T12:00:00`);

const toIsoDate = (date) => {
  const pad = (number) => String(number).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export const weekdayOf = (isoDate) => atLocalNoon(isoDate).getDay();

export const formatDateLabel = (isoDate) =>
  atLocalNoon(isoDate).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

export const formatRangeLabel = (startIsoDate, endIsoDate) => {
  const start = atLocalNoon(startIsoDate);
  const end = atLocalNoon(endIsoDate);
  const sameYear = start.getFullYear() === end.getFullYear();
  const startLabel = start.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
  const endLabel = end.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  return `${startLabel} \u2013 ${endLabel}`;
};

export const formatTimeLabel = (time) => {
  if (!time) return "";
  const [hours, minutes] = time.split(":").map(Number);
  const meridiem = hours < 12 ? "AM" : "PM";
  const displayHour = hours % 12 === 0 ? 12 : hours % 12;
  return `${displayHour}:${String(minutes).padStart(2, "0")} ${meridiem}`;
};

const minutesSinceMidnight = (time) => {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
};

const timeFromMinutes = (minutes) => {
  const wrapped = ((minutes % 1440) + 1440) % 1440;
  return `${String(Math.floor(wrapped / 60)).padStart(2, "0")}:${String(wrapped % 60).padStart(2, "0")}`;
};

const DEFAULT_SHOW_MINUTES = 150;

export const endTimeOf = (show) =>
  show.endTime || (show.showTime ? timeFromMinutes(minutesSinceMidnight(show.showTime) + DEFAULT_SHOW_MINUTES) : "");

export const endsNextDay = (start, end) => Boolean(start && end && end <= start);

export const withShowTime = (show, showTime) => {
  if (!show.endTime || !show.showTime || !showTime) return { showTime };
  const moved = minutesSinceMidnight(showTime) - minutesSinceMidnight(show.showTime);
  return { showTime, endTime: timeFromMinutes(minutesSinceMidnight(show.endTime) + moved) };
};

export const datesInRange = (startDate, endDate, daysOfWeek) => {
  if (!startDate || !endDate || !daysOfWeek.length) return [];
  const start = atLocalNoon(startDate);
  const end = atLocalNoon(endDate);
  if (start > end) return [];

  const dates = [];
  const cursor = new Date(start);
  while (cursor <= end && dates.length < MAX_GENERATED_DATES) {
    if (daysOfWeek.includes(cursor.getDay())) dates.push(toIsoDate(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
};

export const countDatesInRange = (startDate, endDate, daysOfWeek) => {
  if (!startDate || !endDate || !daysOfWeek.length) return 0;
  const start = atLocalNoon(startDate);
  const end = atLocalNoon(endDate);
  if (start > end) return 0;

  let count = 0;
  const cursor = new Date(start);
  while (cursor <= end) {
    if (daysOfWeek.includes(cursor.getDay())) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
};

export const entrySlots = ({ firstEntry, lastEntry, intervalMinutes }) => {
  if (!firstEntry || !lastEntry || !intervalMinutes) return [];
  const first = minutesSinceMidnight(firstEntry);
  const last = minutesSinceMidnight(lastEntry);
  const spanMinutes = last >= first ? last - first : last + 1440 - first;

  const slots = [];
  for (let offset = 0; offset <= spanMinutes; offset += intervalMinutes) {
    slots.push(timeFromMinutes(first + offset));
  }
  return slots;
};

let rowSequence = 0;
const nextRowId = () => {
  rowSequence += 1;
  return `series-schedule-row-${rowSequence}`;
};

export const generateMultipleShows = ({ startDate, endDate, daysOfWeek, showTimes }) =>
  datesInRange(startDate, endDate, daysOfWeek).flatMap((date) =>
    [...(showTimes[weekdayOf(date)] || [])]
      .sort((a, b) => (a.showTime || "").localeCompare(b.showTime || ""))
      .map((show) => ({
        id: nextRowId(),
        date,
        doors: show.doors,
        showTime: show.showTime,
        endTime: show.endTime,
        label: show.label,
      })),
  );

export const generateTimedEntry = ({ startDate, endDate, daysOfWeek, entry }) => {
  const times = entrySlots(entry);
  return datesInRange(startDate, endDate, daysOfWeek).map((date) => ({
    id: nextRowId(),
    date,
    times: [...times],
  }));
};

const SHOW_LABELS = ["Evening", "Matinee", "Late Show", "Twilight"];

export const blankShow = (existingCount = 0) => ({
  id: nextRowId(),
  label: SHOW_LABELS[existingCount % SHOW_LABELS.length],
  doors: "19:00",
  showTime: "20:00",
});

export const clashingShowIds = (shows) => {
  const idsByTime = {};
  shows.forEach((show) => {
    if (!show.showTime) return;
    idsByTime[show.showTime] = [...(idsByTime[show.showTime] || []), show.id];
  });
  return new Set(
    Object.values(idsByTime)
      .filter((ids) => ids.length > 1)
      .flat(),
  );
};

export const SALES_WINDOWS = {
  showDetails: "Based on show details",
  afterSalesEnd: "When sales end for...",
  afterSellOut: "When tickets sell out for...",
  afterEither: "When ticket sales end or sell out for...",
};

export const WAIT_FOR_WINDOWS = [SALES_WINDOWS.afterSalesEnd, SALES_WINDOWS.afterSellOut, SALES_WINDOWS.afterEither];

export const blankDynamicPricing = () => ({ dynamicPricingEnabled: false, dynamicPricingMin: "", dynamicPricingMax: "" });

export const CHECK_IN_PERIOD_OPTIONS = [
  { value: "all_time", label: "All Time" },
  { value: "daily", label: "Daily" },
];

export const blankTicketType = () => ({
  id: nextRowId(),
  clientKey: uuidv4(),
  name: "",
  price: "0",
  inventory: "",
  description: "",
  absorbFees: false,
  roundUp: false,
  availability: null,
  startActivity: "",
  endActivity: "",
  waitForName: "",
  minimumPerOrder: "1",
  maximumPerOrder: "10",
  visibility: "Visible",
  salesChannel: "Everywhere",
  settlement: "",
  checkInLimit: "1",
  hideQrCode: false,
  accessibleSeating: "",
  presaleWindows: [],
  pricePoints: [],
  bundledTicketTypeName: "",
  scanStartActivity: "",
  scanEndActivity: "",
  manualTaxIds: null,
  manifested: true,
  opendateDistribution: false,
  hasFixedFees: false,
  fixedFeeAmount: "",
  checkInPeriod: "",
  ...blankDynamicPricing(),
  startOffsetMinutes: 0,
  endOffsetMinutes: 0,
  scanStartOffsetMinutes: 0,
  scanEndOffsetMinutes: 0,
});

export const DAY_OF_SHOW_NAME = "Day of Show";

const isDayOfShow = (point) => !point.showActivityName;

export const blankPricePoint = () => ({
  id: nextRowId(),
  name: "",
  price: "",
  showActivityName: "",
  offsetMinutes: 0,
});

export const blankPresaleWindow = (fields = {}) => ({
  id: nextRowId(),
  name: "",
  price: "",
  inventory: "",
  startActivity: "",
  endActivity: "Public On-Sale",
  accessCode: "",
  ...fields,
});

export const dayOfShowPrice = (row) => ((row.pricePoints || []).find(isDayOfShow) || {}).price || "";

export const withDayOfShowPrice = (row, price) => {
  const points = row.pricePoints || [];
  const existing = points.find(isDayOfShow);
  const others = points.filter((point) => !isDayOfShow(point));
  if (price === "" || price == null) return { ...row, pricePoints: others };
  return { ...row, pricePoints: [...others, { ...(existing || blankPricePoint()), name: DAY_OF_SHOW_NAME, price }] };
};

export const PROMO_CODE_NAME_FORMAT = /^[A-Z0-9\-_%!]+$/;

const WAIT_FOR_LABELS = {
  [SALES_WINDOWS.afterSalesEnd]: (name) => `After ${name}'s sales end`,
  [SALES_WINDOWS.afterSellOut]: (name) => `After ${name} sells out`,
  [SALES_WINDOWS.afterEither]: (name) => `After ${name} sells out or its sales end`,
};

export const onSaleLabel = (type) => {
  if (WAIT_FOR_LABELS[type.availability] && type.waitForName) return WAIT_FOR_LABELS[type.availability](type.waitForName);
  if (type.availability === SALES_WINDOWS.showDetails && type.startActivity) return `${type.startActivity} → ${type.endActivity}`;
  return "At public on-sale";
};

export const ACCESSIBLE_SEATING_OPTIONS = [
  { value: "", label: "Not an accessible ticket" },
  { value: "accessible", label: "Accessible ticket (e.g. wheelchair area / ADA)" },
  { value: "companion", label: "Companion ticket" },
];

const swapWaitFor = (type, from, to) => {
  if (!WAIT_FOR_WINDOWS.includes(type.availability) || type.waitForName !== from) return type;
  return to ? { ...type, waitForName: to } : { ...type, availability: null, waitForName: "" };
};

const swapBundle = (type, from, to) =>
  type.bundledTicketTypeName && type.bundledTicketTypeName === from ? { ...type, bundledTicketTypeName: to || "" } : type;

export const swapTicketReferences = (ticketTypes, from, to) =>
  ticketTypes.map((type) => swapBundle(swapWaitFor(type, from, to), from, to));

export const TIMED_ENTRY_SHOW_TYPE = "All Dates";
export const UNLABELLED_SHOW_TYPE = "All Shows";

export const showTypesFor = (schedule) => {
  if (schedule.generated.length === 0) return [];
  if (schedule.mode === "timed_entry") return [TIMED_ENTRY_SHOW_TYPE];

  const labels = [...new Set(schedule.generated.map((row) => row.label).filter(Boolean))];
  return labels.length > 0 ? labels : [UNLABELLED_SHOW_TYPE];
};

export const blankShowTypeConfig = () => ({
  started: false,
  ticketTypes: [],
  addOns: [],
  promoCodes: [],
  memberships: {},
  holds: [],
  kills: [],
});

export const configFor = (byShowType, showType) => ({
  ...blankShowTypeConfig(),
  ...((byShowType || {})[showType] || {}),
});

export const blankAddOn = () => ({
  id: nextRowId(),
  clientKey: uuidv4(),
  name: "",
  price: "",
  inventory: "",
  description: "",
  minimumPerOrder: "1",
  maximumPerOrder: "10",
  absorbFees: false,
  roundUp: false,
  requireTicketType: false,
  requiredTicketTypeNames: [],
  confirmationDetails: "",
  transferable: null,
  startActivity: "",
  endActivity: "",
  limitQuantityToAdmissionCount: true,
  disableRequireTicketTypeWithDirectLink: false,
  checkInLimit: "1",
  hideQrCode: false,
  salesChannel: "Everywhere",
  accessibleSeating: "",
  pricePoints: [],
  manualTaxIds: null,
  checkInPeriod: "",
  scanStartActivity: "",
  scanEndActivity: "",
  imageFile: null,
  imageUrl: null,
  imageFileName: null,
  removeImage: false,
  ...blankDynamicPricing(),
  startOffsetMinutes: 0,
  endOffsetMinutes: 0,
  scanStartOffsetMinutes: 0,
  scanEndOffsetMinutes: 0,
});

export const PROMO_CODE_WINDOW_DEFAULTS = { startActivity: "Public On-Sale", endActivity: "Event End" };

export const blankPromoCode = () => ({
  id: nextRowId(),
  clientKey: uuidv4(),
  name: "",
  discountType: "percentage",
  discount: "",
  unlimited: true,
  limit: "",
  ...PROMO_CODE_WINDOW_DEFAULTS,
  appliesToTicketTypes: true,
  appliesToAddOns: false,
  revealHiddenTickets: false,
  revealAllHiddenTickets: false,
  limitToOneTicketPerOrder: false,
  limitToOneAddOnPerOrder: false,
  appliesToEntireOrder: false,
  revealedTicketTypeNames: [],
  revealedHoldNames: [],
  discountsOnlySelectedTicketTypes: false,
  discountedTicketTypeNames: [],
});

export const blankMembershipDiscount = () => ({
  discountType: "percentage",
  discount: "0",
  appliesToTicketTypes: true,
  appliesToAddOns: false,
  ticketLimitPerMember: "",
  revealedTicketTypeNames: [],
  revealedAddOnNames: [],
});

export const settingsFor = (config, venueDefaults) => ({
  deliveryType: "immediate_electronic",
  deliveryTimeInterval: "1",
  deliveryTimeUnit: "weeks",
  maxTicketsPerEmail: "",
  checkInPeriod: "all_time",
  checkoutCaptchaEnabled: false,
  hasAddOnsIntro: false,
  addOnsIntro: "",
  hasAddOnsAlertMessage: false,
  addOnsAlertMessage: "",
  ...(venueDefaults || {}),
  ...(config.settings || {}),
});

export const DELAYED_DELIVERY_TYPES = ["delayed_email", "delayed_electronic"];

const priceNamesOf = (config, name) => {
  const section = (config.ticketTypes || []).find((type) => type.name === name && type.sectionKey);
  return ((section && section.priceOptions) || []).map((option) => `${name} - ${(option.label || "").trim()}`);
};

const swapName = (names, from, to, priceNames = []) =>
  (names || []).flatMap((name) => {
    if (name !== from && !priceNames.includes(name)) return [name];
    return to ? [`${to}${name.slice(from.length)}`] : [];
  });

const swapInDiscount = (discount, key, from, to, priceNames = []) =>
  discount && { ...discount, [key]: swapName(discount[key], from, to, priceNames) };

export const renameReferences = (config, kind, from, to) => {
  if (!from) return {};
  if (from === to) return {};
  if (kind === "hold") {
    return { promoCodes: (config.promoCodes || []).map((code) => swapInDiscount(code, "revealedHoldNames", from, to)) };
  }
  const prices = kind === "ticketType" ? priceNamesOf(config, from) : [];
  const key = kind === "ticketType" ? "revealedTicketTypeNames" : "revealedAddOnNames";
  const memberships = Object.fromEntries(
    Object.entries(config.memberships || {}).map(([id, membership]) => [
      id,
      { ...membership, discount: swapInDiscount(membership.discount, key, from, to, prices) },
    ]),
  );

  return {
    promoCodes: (config.promoCodes || []).map((code) =>
      kind === "ticketType"
        ? swapInDiscount(swapInDiscount(code, key, from, to, prices), "discountedTicketTypeNames", from, to, prices)
        : code,
    ),
    memberships,
    ...(kind === "ticketType" && {
      holds: (config.holds || []).flatMap((hold) =>
        hold.ticketTypeName !== from ? [hold] : to ? [{ ...hold, ticketTypeName: to }] : [],
      ),
      addOns: (config.addOns || []).map((addOn) => {
        const requiredTicketTypeNames = swapName(addOn.requiredTicketTypeNames, from, to, prices);
        return {
          ...addOn,
          requiredTicketTypeNames,
          requireTicketType: Boolean(addOn.requireTicketType) && requiredTicketTypeNames.length > 0,
        };
      }),
    }),
  };
};

const unpickTaxes = (row) => ({ ...row, manualTaxIds: null });

export const withTaxesUnpicked = (tickets) => ({
  ...tickets,
  byShowType: Object.fromEntries(
    Object.entries(tickets.byShowType || {}).map(([showType, config]) => [
      showType,
      { ...config, ticketTypes: (config.ticketTypes || []).map(unpickTaxes), addOns: (config.addOns || []).map(unpickTaxes) },
    ]),
  ),
});

export const allTicketTypes = (byShowType) =>
  Object.values(byShowType || {}).flatMap((config) => config.ticketTypes || []);

export const sortTimesForDate = (times, { firstEntry, lastEntry } = {}) => {
  if (!firstEntry || !lastEntry) {
    return [...times].sort((a, b) => minutesSinceMidnight(a) - minutesSinceMidnight(b));
  }

  const first = minutesSinceMidnight(firstEntry);
  const span = (minutesSinceMidnight(lastEntry) - first + 1440) % 1440;
  const closed = 1440 - span;
  const key = (time) => {
    const sinceOpen = (minutesSinceMidnight(time) - first + 1440) % 1440;
    if (sinceOpen <= span) return sinceOpen;
    return sinceOpen - span <= closed / 2 ? sinceOpen : sinceOpen - 1440;
  };
  return [...times].sort((a, b) => key(a) - key(b));
};

const timedSets = (tickets) => ticketSetsOf(configFor((tickets || {}).byShowType, TIMED_ENTRY_SHOW_TYPE));

export const scheduleSlotRows = (schedule, tickets) => {
  if (schedule.mode === "timed_entry") {
    const cover = coverage(schedule, timedSets(tickets));
    return schedule.generated.flatMap((row) =>
      (row.times || []).map((time) => ({
        date: row.date,
        time_of_day: time,
        show_type: TIMED_ENTRY_SHOW_TYPE,
        ticket_set_key: slotKeyFor(cover, row.date, time),
      })),
    );
  }

  return schedule.generated.map((row) => ({
    date: row.date,
    time_of_day: row.showTime,
    door_time_of_day: row.doors || "",
    end_time_of_day: endTimeOf(row),
    show_type: row.label || UNLABELLED_SHOW_TYPE,
  }));
};

const isBlank = (value) => value === "" || value == null || !Number.isFinite(Number(value));

const wholeNumber = (value, { min = 0, max = Infinity } = {}) =>
  isBlank(value) ? null : Math.min(max, Math.max(min, Math.round(Number(value))));

const money = (value) => (isBlank(value) ? null : Math.max(0, Math.round(Number(value) * 100) / 100));

const trimmedNames = (names) => (names || []).map((name) => (name || "").trim()).filter(Boolean);

export const plannedTicketTypeRows = (schedule, tickets, templateIds = {}) => {
  const sets = schedule.mode === "timed_entry" ? timedSets(tickets) : null;
  return showTypesFor(schedule).flatMap((showType) =>
    configFor(tickets.byShowType, showType).ticketTypes.map((type) => ({
      show_type: showType,
      ...(sets && { ticket_set_key: ticketSetKeyOf(type, sets) }),
      name: (type.name || "").trim(),
      price: money(type.price) || 0,
      free: type.sectionKey ? Boolean(type.free) && !money(type.price) : !money(type.price),
      available: wholeNumber(type.inventory),
      description: (type.description || "").trim(),
      absorb_fees: Boolean(money(type.price) && type.absorbFees),
      round_up: Boolean(money(type.price) && type.roundUp),
      seating_chart_category_key: type.sectionKey || null,
      ...ticketSaleWindow(type),
      minimum_tickets_per_order: orderLimitsFor(type).minimum,
      maximum_tickets_per_order: orderLimitsFor(type).maximum,
      visibility: type.visibility || "Visible",
      sales_channel: type.sectionKey ? "Everywhere" : type.salesChannel || "Everywhere",
      settlement_amount: money(type.price) ? money(type.settlement) : null,
      check_in_limit: wholeNumber(type.checkInLimit) ?? 1,
      hide_qr_code: Boolean(type.hideQrCode),
      accessible_seating: type.accessibleSeating || null,
      presale_windows: plannedPresaleWindowRows(type),
      price_options: type.sectionKey ? plannedPriceOptionRows(type, schedule) : [],
      price_points: money(type.price) ? plannedPricePointRows(type.pricePoints) : [],
      planned_bundled_ticket_type_name: (!type.sectionKey && type.bundledTicketTypeName) || null,
      ...scanWindow(type, schedule),
      manual_tax_ids: plannedManualTaxIds(type),
      manifested: type.manifested !== false,
      opendate_distribution: !type.sectionKey && Boolean(type.opendateDistribution),
      ...fixedFees(type),
      check_in_period: plannedCheckInPeriod(type),
      ...plannedDynamicPricing(type),
      should_be_default: !type.sectionKey && Boolean(type.shouldBeDefault),
      template_id: carriedTemplateId(type, templateIds),
      client_key: type.clientKey || null,
    })),
  );
};

const offsetOf = (minutes) => Math.round(Number(minutes) || 0) || null;

export const showDetailTimeRows = (times, offsets) => [
  ...Object.entries(times || {})
    .filter(([name, startTime]) => Boolean(startTime) && !Number.isInteger((offsets || {})[name]))
    .map(([name, startTime]) => ({ name, start_time: startTime })),
  ...Object.entries(offsets || {})
    .filter(([, minutes]) => Number.isInteger(minutes))
    .map(([name, minutes]) => ({ name, offset_minutes: minutes })),
];

const plannedCheckInPeriod = (row) =>
  CHECK_IN_PERIOD_OPTIONS.some((option) => option.value === row.checkInPeriod) ? row.checkInPeriod : null;

const fixedFees = (type) => {
  const set = Boolean(type.hasFixedFees && money(type.price));
  return { has_fixed_fees: set, fixed_fee_amount: set ? money(type.fixedFeeAmount) || 0 : 0 };
};

export const dynamicPricingBounds = (row) => {
  const price = money(row.price);
  if (!row.dynamicPricingEnabled || !price) return null;
  return { min: money(row.dynamicPricingMin) ?? price, max: money(row.dynamicPricingMax) ?? Math.round(price * 300) / 100 };
};

const plannedDynamicPricing = (row) => {
  const bounds = dynamicPricingBounds(row);
  return {
    dynamic_pricing_enabled: Boolean(bounds),
    dynamic_pricing_min: bounds ? bounds.min : null,
    dynamic_pricing_max: bounds ? bounds.max : null,
  };
};

const scanWindow = (type, schedule) => {
  const set = schedule.mode !== "timed_entry" && Boolean(type.scanStartActivity) && Boolean(type.scanEndActivity);
  return {
    planned_timed_entry_start_show_activity_name: set ? type.scanStartActivity : null,
    planned_timed_entry_end_show_activity_name: set ? type.scanEndActivity : null,
    planned_timed_entry_start_offset_minutes: set ? offsetOf(type.scanStartOffsetMinutes) : null,
    planned_timed_entry_end_offset_minutes: set ? offsetOf(type.scanEndOffsetMinutes) : null,
  };
};

const plannedManualTaxIds = (row) => (Array.isArray(row.manualTaxIds) ? row.manualTaxIds.map(Number) : null);

const plannedPresaleWindowRows = (type) =>
  (type.presaleWindows || []).map((window) => ({
    name: (window.name || "").trim(),
    price: money(window.price) ?? money(type.price) ?? 0,
    available: wholeNumber(window.inventory),
    planned_start_show_activity_name: window.startActivity || null,
    planned_end_show_activity_name: window.endActivity || null,
    planned_start_offset_minutes: window.startActivity ? offsetOf(window.startOffsetMinutes) : null,
    planned_end_offset_minutes: window.endActivity ? offsetOf(window.endOffsetMinutes) : null,
    planned_access_code: (window.accessCode || "").trim().toUpperCase() || null,
  }));

const plannedPriceOptionRows = (type, schedule) =>
  (type.priceOptions || []).map((option) => ({
    name: (option.label || "").trim(),
    price: money(option.price),
    description: (option.description || "").trim(),
    maximum_tickets_per_order: wholeNumber(option.maximumPerOrder),
    ...(option.scanStartActivity && option.scanEndActivity ? scanWindow(option, schedule) : {}),
  }));

const plannedPricePointRows = (points) =>
  (points || [])
    .filter((point) => !isBlank(point.price))
    .map((point) => ({
      name: (point.name || "").trim() || (isDayOfShow(point) ? DAY_OF_SHOW_NAME : null),
      price: money(point.price),
      planned_show_activity_name: point.showActivityName || null,
      planned_offset_minutes: point.showActivityName ? Math.round(Number(point.offsetMinutes) || 0) : null,
    }));

const orderLimitsFor = (row) => {
  const maximum = wholeNumber(row.maximumPerOrder) ?? 10;
  return { minimum: Math.min(wholeNumber(row.minimumPerOrder) ?? 1, maximum), maximum };
};

const ticketSaleWindow = (type) => {
  if (WAIT_FOR_WINDOWS.includes(type.availability) && type.waitForName) {
    return { planned_availability: type.availability, planned_wait_for_ticket_type_name: type.waitForName };
  }
  if (type.availability === SALES_WINDOWS.showDetails && type.startActivity && type.endActivity) {
    return {
      planned_availability: type.availability,
      planned_start_show_activity_name: type.startActivity,
      planned_end_show_activity_name: type.endActivity,
      planned_start_offset_minutes: offsetOf(type.startOffsetMinutes),
      planned_end_offset_minutes: offsetOf(type.endOffsetMinutes),
    };
  }
  return { planned_availability: null };
};

const cents = (value) => {
  const amount = Math.round((money(value) || 0) * 100);
  return amount > 0 ? amount : null;
};

const discountFields = (discount) => ({
  amount: discount.discountType === "amount" ? money(discount.discount) : null,
  percentage: discount.discountType === "percentage" ? wholeNumber(discount.discount, { max: 100 }) : null,
  applies_to_ticket_types: Boolean(discount.appliesToTicketTypes),
  applies_to_add_ons: Boolean(discount.appliesToAddOns),
  revealed_ticket_type_names: trimmedNames(discount.revealedTicketTypeNames),
  revealed_add_on_names: trimmedNames(discount.revealedAddOnNames),
});

// Promo codes reveal ticket types only, as on a single show; add-on reveals are a membership-discount option.
const promoCodeDiscountFields = (code) => {
  const { revealed_add_on_names: _addOns, ...fields } = discountFields(code);
  return fields;
};

export const plannedAddOnRows = (schedule, tickets) =>
  showTypesFor(schedule).flatMap((showType) =>
    configFor(tickets.byShowType, showType).addOns.map((addOn) => ({
      show_type: showType,
      name: (addOn.name || "").trim(),
      price: money(addOn.price) || 0,
      available: wholeNumber(addOn.inventory),
      description: (addOn.description || "").trim(),
      minimum_per_order: Math.min(addOn.sectionKey ? 1 : wholeNumber(addOn.minimumPerOrder) ?? 0, wholeNumber(addOn.maximumPerOrder) ?? 0),
      maximum_per_order: wholeNumber(addOn.maximumPerOrder),
      absorb_fees: Boolean(money(addOn.price) && addOn.absorbFees),
      round_up: Boolean(money(addOn.price) && addOn.roundUp),
      require_ticket_type: Boolean(addOn.requireTicketType),
      required_ticket_type_names: addOn.requireTicketType ? trimmedNames(addOn.requiredTicketTypeNames) : [],
      confirmation_details: (addOn.confirmationDetails || "").trim(),
      transferable: addOn.transferable ?? null,
      planned_start_show_activity_name: (addOn.startActivity && addOn.endActivity && addOn.startActivity) || null,
      planned_end_show_activity_name: (addOn.startActivity && addOn.endActivity && addOn.endActivity) || null,
      planned_start_offset_minutes: addOn.startActivity && addOn.endActivity ? offsetOf(addOn.startOffsetMinutes) : null,
      planned_end_offset_minutes: addOn.startActivity && addOn.endActivity ? offsetOf(addOn.endOffsetMinutes) : null,
      limit_quantity_to_admission_count: addOn.limitQuantityToAdmissionCount !== false,
      disable_require_ticket_type_with_direct_link: Boolean(addOn.requireTicketType && addOn.disableRequireTicketTypeWithDirectLink),
      check_in_limit: wholeNumber(addOn.checkInLimit) ?? 1,
      hide_qr_code: Boolean(addOn.hideQrCode),
      accessible_seating: addOn.accessibleSeating || null,
      sales_channel: addOn.salesChannel || "Everywhere",
      price_points: money(addOn.price) ? plannedPricePointRows(addOn.pricePoints) : [],
      manual_tax_ids: plannedManualTaxIds(addOn),
      seating_chart_category_key: addOn.sectionKey || null,
      check_in_period: plannedCheckInPeriod(addOn),
      ...plannedDynamicPricing(addOn),
      ...scanWindow(addOn, schedule),
      client_key: addOn.clientKey || null,
      remove_image: Boolean(addOn.removeImage && !addOn.imageFile),
    })),
  );

export const pendingAddOnImages = (schedule, tickets, uploaded) =>
  showTypesFor(schedule).flatMap((showType) =>
    configFor(tickets.byShowType, showType).addOns.filter(
      (addOn) => addOn.imageFile && addOn.clientKey && !uploaded.has(addOn.imageFile),
    ),
  );

const orderLimits = (code) => {
  const entireOrder = Boolean(code.discountType === "amount" && code.appliesToEntireOrder);
  const oneTicket = Boolean(code.limitToOneTicketPerOrder) && !entireOrder;
  const someTickets = entireOrder ? null : (code.discountedTicketsPerOrder ?? null);
  return {
    applies_to_entire_order: entireOrder,
    limit_to_one_ticket_per_order: oneTicket,
    discounted_tickets_per_order: someTickets,
    limit_to_one_add_on_per_order: Boolean(code.limitToOneAddOnPerOrder) && !entireOrder && !oneTicket && !someTickets,
  };
};

export const plannedPromoCodeRows = (schedule, tickets, templateIds = {}) =>
  showTypesFor(schedule).flatMap((showType) =>
    configFor(tickets.byShowType, showType).promoCodes.map((code) => ({
      show_type: showType,
      name: (code.name || "").trim().toUpperCase(),
      ...promoCodeDiscountFields(code),
      unlimited: Boolean(code.unlimited),
      limit: code.unlimited ? null : wholeNumber(code.limit),
      start_show_activity_name: code.startActivity,
      end_show_activity_name: code.endActivity,
      planned_start_offset_minutes: code.startActivity ? offsetOf(code.startOffsetMinutes) : null,
      planned_end_offset_minutes: code.endActivity ? offsetOf(code.endOffsetMinutes) : null,
      should_be_default: Boolean(code.shouldBeDefault),
      template_id: carriedTemplateId(code, templateIds),
      client_key: code.clientKey || null,
      reveal_hidden_tickets: Boolean(code.revealHiddenTickets),
      revealed_hold_names: code.revealHiddenTickets ? trimmedNames(code.revealedHoldNames) : [],
      ...(!code.revealHiddenTickets && { revealed_ticket_type_names: [] }),
      reveal_all_hidden_tickets: Boolean(code.revealHiddenTickets && code.revealAllHiddenTickets && code.appliesToTicketTypes),
      ...selectedTicketDiscount(code),
      ...orderLimits(code),
    })),
  );

const selectedTicketDiscount = (code) => {
  const names = code.appliesToTicketTypes && code.discountsOnlySelectedTicketTypes ? trimmedNames(code.discountedTicketTypeNames) : [];
  return { discounts_only_selected_ticket_types: names.length > 0, discounted_ticket_type_names: names };
};

const membershipDiscount = (discount) => ({
  ...discount,
  discount: isBlank(discount.discount) ? "0" : discount.discount,
});

export const plannedMembershipRows = (schedule, tickets) =>
  showTypesFor(schedule).flatMap((showType) =>
    Object.entries(configFor(tickets.byShowType, showType).memberships).map(([productId, membership]) => ({
      show_type: showType,
      membership_product_id: Number(productId),
      enabled: membership.enabled !== false,
      price_cents: cents(membership.price),
      monthly_price_cents: cents(membership.monthlyPrice),
      discount: membership.discount
        ? {
            ...discountFields(membershipDiscount(membership.discount)),
            ticket_limit_per_member: wholeNumber(membership.discount.ticketLimitPerMember),
          }
        : null,
    })),
  );

export const showTypeSettingRows = (schedule, tickets, venueDefaults) =>
  showTypesFor(schedule).map((showType) => {
    const config = configFor(tickets.byShowType, showType);
    const settings = settingsFor(config, venueDefaults);
    const delayed = DELAYED_DELIVERY_TYPES.includes(settings.deliveryType);
    return {
      show_type: showType,
      delivery_type: settings.deliveryType,
      delivery_time_interval: delayed ? wholeNumber(settings.deliveryTimeInterval, { min: 1 }) || 1 : null,
      delivery_time_unit: delayed ? settings.deliveryTimeUnit : null,
      max_tickets_per_email: wholeNumber(settings.maxTicketsPerEmail),
      check_in_period: settings.checkInPeriod,
      checkout_captcha_enabled: Boolean(settings.checkoutCaptchaEnabled),
      seating_chart_id: tickets.seatingMode === "seated" ? config.seatingChartId || null : null,
      add_on_seating_chart_id: config.addOnSeatingChartId || null,
      has_add_ons_intro: Boolean(settings.hasAddOnsIntro),
      add_ons_intro: settings.addOnsIntro || null,
      has_add_ons_alert_message: Boolean(settings.hasAddOnsAlertMessage),
      add_ons_alert_message: settings.addOnsAlertMessage || null,
    };
  });

const isBlankInventory = (type) => type.inventory === "" || type.inventory == null;

export const inventoryLabel = (type, schedule) => {
  if (type.sectionKey) return `${type.inventory || 0} seats`;
  if (!isBlankInventory(type)) return String(type.inventory);
  if (schedule.mode === "timed_entry") return `Up to ${schedule.entry.capacity || "—"} (shared)`;
  return "Shares show capacity";
};

export const capacitySummary = (schedule, capacity) => {
  if (schedule.mode === "timed_entry") {
    const perWindow = Number(schedule.entry.capacity);
    if (!perWindow) return null;
    const busiest = Math.max(0, ...schedule.generated.map((row) => (row.times || []).length));
    return `${perWindow} per entry time · up to ${perWindow * busiest} per date`;
  }
  return capacity ? `Capacity ${capacity} per show` : null;
};

export const replaceTicketTypes = (config, ticketTypes) => {
  const kept = new Set(ticketTypes.map((type) => type.name));
  const removed = (config.ticketTypes || []).filter((type) => !kept.has(type.name));
  const cleaned = removed.reduce((next, type) => ({ ...next, ...renameReferences(next, "ticketType", type.name) }), config);

  return {
    ...cleaned,
    started: true,
    ticketTypes: removed.reduce((types, type) => swapTicketReferences(types, type.name), ticketTypes),
  };
};

export const sectionTicketTypes = (sections, previous = []) =>
  sections.map((section) => {
    const before = previous.find((type) => type.sectionKey === section.key);
    return {
      ...blankTicketType(),
      name: section.label,
      price: "",
      ...before,
      sectionKey: section.key,
      inventory: String(section.count),
    };
  });

export const ADD_ON_CHART = "add_on";

export const chartContextOf = (row) => (row.chartContext === ADD_ON_CHART ? ADD_ON_CHART : null);

const onAddOnChart = (row) => chartContextOf(row) === ADD_ON_CHART;

export const keepRowsForSeating = (config, seatingMode) => {
  const seated = seatingMode === "seated";
  const kept = (config.ticketTypes || []).filter((type) => Boolean(type.sectionKey) === seated);
  const next = replaceTicketTypes(config, kept);
  const holds = (next.holds || []).filter((hold) => onAddOnChart(hold) || Boolean(hold.objects) === seated);
  const droppedHolds = (next.holds || []).filter((hold) => !holds.includes(hold));
  return {
    ...next,
    ...droppedHolds.reduce((refs, hold) => ({ ...refs, ...renameReferences({ ...next, ...refs }, "hold", hold.name) }), {}),
    started: kept.length > 0,
    seatingChartId: seated ? config.seatingChartId : undefined,
    holds,
    kills: (next.kills || []).filter((kill) => seated || onAddOnChart(kill)),
  };
};

export const sectionAddOns = (sections, addOns = []) => {
  const general = addOns.filter((addOn) => !addOn.sectionKey);
  const kept = addOns.filter((addOn) => addOn.sectionKey && sections.some((section) => section.key === addOn.sectionKey));
  const taken = new Set([...general, ...kept].map((addOn) => (addOn.name || "").trim()));
  const uniqueName = (label) => {
    let name = label;
    for (let copy = 2; taken.has(name); copy += 1) name = `${label} ${copy}`;
    taken.add(name);
    return name;
  };
  return [
    ...general,
    ...sections.map((section) => {
      const before = addOns.find((addOn) => addOn.sectionKey === section.key);
      return {
        ...blankAddOn(),
        ...(before || { name: uniqueName(section.label) }),
        sectionKey: section.key,
        inventory: String(section.count),
      };
    }),
  ];
};

export const withAddOnChart = (config, addOnSeatingChartId, sections = []) => {
  const swapped = config.addOnSeatingChartId !== addOnSeatingChartId;
  const before = config.addOns || [];
  const addOns = addOnSeatingChartId
    ? sectionAddOns(sections, swapped ? before.filter((addOn) => !addOn.sectionKey) : before)
    : before.filter((addOn) => !addOn.sectionKey);
  const removed = before.filter((addOn) => !addOns.some((kept) => kept.name === addOn.name));
  const next = removed.reduce((current, addOn) => ({ ...current, ...renameReferences(current, "addOn", addOn.name) }), config);
  return {
    ...next,
    addOns,
    addOnSeatingChartId: addOnSeatingChartId || undefined,
    ...(swapped && {
      holds: (next.holds || []).filter((hold) => !onAddOnChart(hold)),
      kills: (next.kills || []).filter((kill) => !onAddOnChart(kill)),
    }),
  };
};

export const withSeatingMode = (tickets, seatingMode) => ({
  ...tickets,
  seatingMode,
  byShowType: Object.fromEntries(
    Object.entries(tickets.byShowType || {}).map(([showType, config]) => [
      showType,
      keepRowsForSeating(configFor(tickets.byShowType, showType), seatingMode),
    ]),
  ),
});

export const plannedHoldRows = (schedule, tickets) =>
  showTypesFor(schedule).flatMap((showType) =>
    configFor(tickets.byShowType, showType).holds.map((hold) => ({
      show_type: showType,
      name: (hold.name || "").trim(),
      release_offset_minutes: hold.releaseOffsetMinutes || null,
      chart_context: chartContextOf(hold),
      ...(tickets.seatingMode === "seated" || onAddOnChart(hold)
        ? { color: hold.color || null, objects: hold.objects || [] }
        : { ticket_type_name: hold.ticketTypeName, quantity: wholeNumber(hold.quantity, { min: 1 }) }),
    })),
  );

export const plannedKillRows = (schedule, tickets) =>
  showTypesFor(schedule).flatMap((showType) =>
    configFor(tickets.byShowType, showType)
      .kills.filter((kill) => tickets.seatingMode === "seated" || onAddOnChart(kill))
      .map((kill) => ({
        show_type: showType,
        name: (kill.name || "").trim(),
        objects: kill.objects || [],
        area_places: kill.areaPlaces || {},
        chart_context: chartContextOf(kill),
      })),
  );

const KILLED_SEAT_COLOR = "#9aa3b2";
const HELD_SEAT_COLOR = "#7f56d9";

export const takenSeats = ({ holds = [], kills = [], exceptId, chartContext = null }) => {
  const taken = {};
  const sameChart = (row) => chartContextOf(row) === chartContext;
  holds
    .filter((hold) => hold.id !== exceptId && sameChart(hold))
    .forEach((hold) =>
      (hold.objects || []).forEach((label) => {
        taken[label] = { owner: `the hold “${hold.name}”`, color: hold.color || HELD_SEAT_COLOR };
      }),
    );
  kills
    .filter((kill) => kill.id !== exceptId && sameChart(kill))
    .forEach((kill) =>
      (kill.objects || []).forEach((label) => {
        taken[label] = { owner: `the kill “${kill.name}”`, color: KILLED_SEAT_COLOR };
      }),
    );
  return taken;
};

// "Copy 1 of Parking", "Copy 2 of Parking", ...: the name a confirm gives a copy (AddOn#build_copy).
export const copyName = (name, takenNames) => {
  const taken = new Set(takenNames.map((taken) => (taken || "").trim()));
  let copy = 1;
  while (taken.has(`Copy ${copy} of ${name}`)) copy += 1;
  return `Copy ${copy} of ${name}`;
};

export const moveRow = (rows, from, to) => {
  const next = [...rows];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

export const dynamicPricingProblem = (row) => {
  const bounds = dynamicPricingBounds(row);
  if (!bounds) return null;

  const price = Number(row.price);
  if (bounds.min > price) return "The minimum can't be above the price.";
  if (bounds.max < price) return "The maximum can't be below the price.";
  if (bounds.min >= bounds.max) return "The minimum has to be less than the maximum.";
  const outside = (row.pricePoints || [])
    .filter((point) => point.price !== "" && point.price != null)
    .some((point) => Number(point.price) < bounds.min || Number(point.price) > bounds.max);
  return outside ? "Every price change has to stay between the minimum and the maximum." : null;
};

// What the last save said a row links to beats the id the row loaded with,
// which goes stale once the row is unticked.
const carriedTemplateId = (row, templateIds) =>
  (Object.prototype.hasOwnProperty.call(templateIds, row.id) ? templateIds[row.id] : row.templateId) || null;

// The server answers each save with the template every ticked row links to,
// by show type and name. Remember them by the client's row id, which a rename
// doesn't change, so the next save carries the link to the renamed row.
export const templateIdsByRow = (tickets, planTemplateIds, known = {}) => {
  if (!planTemplateIds) return known;
  const ids = { ...known };
  const remember = (rows, saved, showType, nameOf, setKeyOf = () => null) =>
    (rows || []).forEach((row) => {
      const match = (saved || []).find(
        (entry) =>
          entry.show_type === showType &&
          entry.name === nameOf(row) &&
          (entry.ticket_set_key == null || entry.ticket_set_key === setKeyOf(row)),
      );
      ids[row.id] = match ? match.template_id : null;
    });
  Object.entries(tickets.byShowType || {}).forEach(([showType, config]) => {
    const sets = ticketSetsOf(config);
    remember(config.ticketTypes, planTemplateIds.ticket_types, showType, (row) => (row.name || "").trim(), (row) => ticketSetKeyOf(row, sets));
    remember(config.promoCodes, planTemplateIds.promo_codes, showType, (row) => (row.name || "").trim().toUpperCase());
  });
  return ids;
};

const TEMPLATE_DROPPED = ["id", "clientKey", "imageFile", "imageUrl", "imageFileName", "removeImage", "templateId", "shouldBeDefault", "ticketSetKey"];

const templateRow = (row) => {
  const kept = Object.fromEntries(Object.entries(row).filter(([key]) => !TEMPLATE_DROPPED.includes(key)));
  ["presaleWindows", "pricePoints"].forEach((key) => {
    if (kept[key]) kept[key] = kept[key].map(templateRow);
  });
  return kept;
};

export const templateSnapshot = (config) => ({
  ticketTypes: (config.ticketTypes || []).filter((type) => !type.sectionKey).map(templateRow),
  addOns: (config.addOns || []).filter((addOn) => !addOn.sectionKey).map(templateRow),
  promoCodes: (config.promoCodes || []).map(templateRow),
  holds: (config.holds || []).filter((hold) => !chartContextOf(hold)).map(templateRow),
  kills: (config.kills || []).filter((kill) => !chartContextOf(kill)).map(templateRow),
  memberships: { ...(config.memberships || {}) },
  settings: { ...(config.settings || {}) },
});

export const applyTemplateConfig = (snapshot, { manualTaxIds = [], membershipProductIds = [] } = {}) => {
  const taxes = (ids) => {
    if (!Array.isArray(ids)) return null;
    if (manualTaxIds == null) return ids;
    const kept = ids.filter((id) => manualTaxIds.includes(id));
    return kept.length === 0 && ids.length > 0 ? null : kept;
  };
  const withIds = (rows) => (rows || []).map((row) => ({ ...row, id: nextRowId() }));
  const pricedRow = (blank, row) => ({
    ...blank,
    ...row,
    id: blank.id,
    ...(blank.clientKey && { clientKey: blank.clientKey }),
    pricePoints: withIds(row.pricePoints),
    manualTaxIds: taxes(row.manualTaxIds),
  });

  const ticketTypes = (snapshot.ticketTypes || []).map((type) => ({
      ...pricedRow(blankTicketType(), type),
      presaleWindows: withIds(type.presaleWindows),
    }));

  return {
    ...blankShowTypeConfig(),
    started: ticketTypes.length > 0,
    ticketTypes,
    addOns: (snapshot.addOns || []).map((addOn) => pricedRow(blankAddOn(), addOn)),
    promoCodes: (snapshot.promoCodes || []).map((code) => ({ ...blankPromoCode(), ...code, id: nextRowId() })),
    holds: withIds(snapshot.holds),
    kills: withIds(snapshot.kills),
    memberships: Object.fromEntries(
      Object.entries(snapshot.memberships || {}).filter(([productId]) => membershipProductIds.includes(Number(productId))),
    ),
    settings: { ...(snapshot.settings || {}) },
  };
};

export const withTicketTemplate = (current, snapshot, scope) => {
  const applied = applyTemplateConfig(snapshot, scope);
  const ticketNames = applied.ticketTypes.map((type) => type.name);
  const sectionAddOns = (current.addOns || [])
    .filter((addOn) => addOn.sectionKey)
    .map((addOn) => {
      const requiredTicketTypeNames = (addOn.requiredTicketTypeNames || []).filter((name) => ticketNames.includes(name));
      return { ...addOn, requiredTicketTypeNames, requireTicketType: addOn.requireTicketType && requiredTicketTypeNames.length > 0 };
    });
  return {
    ...current,
    ...applied,
    addOns: [...applied.addOns, ...sectionAddOns],
    holds: [...applied.holds, ...(current.holds || []).filter(chartContextOf)],
    kills: [...applied.kills, ...(current.kills || []).filter(chartContextOf)],
  };
};

const nameStillUsed = (ticketTypes, name) => ticketTypes.some((type) => type.name === name);

export const ticketTypeReferences = (config, remaining, from, to) =>
  nameStillUsed(remaining, from) ? {} : renameReferences(config, "ticketType", from, to);

export const withSetTicketTypes = (config, key, nextTypes) => {
  const sets = ticketSetsOf(config);
  const before = typesInSet(config.ticketTypes, sets, key);
  const others = (config.ticketTypes || []).filter((type) => ticketSetKeyOf(type, sets) !== key);
  const stamped = nextTypes.map((type) => (key === sets[0].key && !type.ticketSetKey ? type : { ...type, ticketSetKey: key }));
  const remaining = [...others, ...stamped];
  const removed = before.filter((type) => !stamped.some((kept) => kept.name === type.name));
  const cleaned = removed.reduce((next, type) => ({ ...next, ...ticketTypeReferences(next, remaining, type.name) }), config);
  const swapped = removed.reduce((types, type) => swapTicketReferences(types, type.name), stamped);
  return {
    ...cleaned,
    ticketTypes: sets.flatMap((set) => (set.key === key ? swapped : others.filter((type) => ticketSetKeyOf(type, sets) === set.key))),
  };
};

export const withoutTicketType = (config, typeId) => {
  const removed = (config.ticketTypes || []).find((type) => type.id === typeId);
  if (!removed) return config;
  const sets = ticketSetsOf(config);
  const key = ticketSetKeyOf(removed, sets);
  return withSetTicketTypes(
    config,
    key,
    typesInSet(config.ticketTypes, sets, key).filter((type) => type.id !== typeId),
  );
};

export const withoutTicketSetConfig = (config, key) => {
  const sets = ticketSetsOf(config);
  const emptied = withSetTicketTypes(config, key, []);
  return {
    ...emptied,
    ticketTypes: emptied.ticketTypes.filter((type) => ticketSetKeyOf(type, sets) !== key),
    ticketSets: withoutTicketSet(sets, key),
  };
};

export const timedEntryProblems = (schedule, tickets, seriesAge) => {
  if (schedule.mode !== "timed_entry" || schedule.generated.length === 0) return [];
  const config = configFor(tickets.byShowType, TIMED_ENTRY_SHOW_TYPE);
  const sets = ticketSetsOf(config);
  return ticketSetProblems({ sets, ticketTypes: config.ticketTypes, cover: coverage(schedule, sets, seriesAge) });
};

export const emptiedByRemoval = (before, after, membershipName = () => "a membership discount") => [
  ...(before.promoCodes || []).filter((code, index) =>
    code.discountsOnlySelectedTicketTypes && (code.discountedTicketTypeNames || []).length > 0 &&
    ((after.promoCodes || [])[index]?.discountedTicketTypeNames || []).length === 0)
    .map((code) => ({ name: code.name, names: code.discountedTicketTypeNames })),
  ...(before.addOns || []).filter((addOn, index) => addOn.requireTicketType && !(after.addOns || [])[index]?.requireTicketType)
    .map((addOn) => ({ name: addOn.name, names: addOn.requiredTicketTypeNames || [] })),
  ...Object.entries(before.memberships || {}).filter(([id, membership]) =>
    membership.discount?.appliesToTicketTypes && (membership.discount.revealedTicketTypeNames || []).length > 0 &&
    ((after.memberships || {})[id]?.discount?.revealedTicketTypeNames || []).length === 0)
    .map(([id, membership]) => ({ name: membershipName(id), names: membership.discount.revealedTicketTypeNames })),
];

export const deletionBlockedMessage = (config, references, name, membershipName) => {
  const emptied = emptiedByRemoval(config, { ...config, ...references }, membershipName);
  if (emptied.length === 0) return null;
  return `Can't delete ${name}: ${emptied.map((row) => row.name).join(", ")} ${emptied.length > 1 ? "apply" : "applies"} only to it. Change ${emptied.length > 1 ? "those" : "that"} first.`;
};
